/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

// Component imports
import { Button, Col, Popconfirm, Row, Space, Table, Tooltip } from 'antd';
import { SyncOutlined } from '@ant-design/icons';
import Permissioned from '../Permissioned';
import AudienceForm from '../Forms/AudienceForm';
import SideModal from '../Layout/SideModal';
import { EditIcon } from '../Icons/EditIcon';
import { DeleteIcon } from '../Icons/DeleteIcon';
import { PlusIcon } from '../Icons/PlusIcon';

// Misc imports
import { useDeleteGroupMutation, useFetchGroupsQuery } from '../../store/groups/groupsApi';
import { PERMISSIONS_GROUP_ADMIN, PERMISSIONS_GROUP_EDITOR, DEFAULT_DATETIME_TIMEZONE_FORMAT, DEFAULT_PAGINATION_SIZE } from '../../constants/main';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { useDragDetection } from '../../utils/hooks/useDragDetection';
import useUserPermissions from '../../utils/hooks/useUserPermissions';
import getSupportedPlatforms from '../../utils/misc/getSupportedPlatforms';

const AudienceTable = () => {
  const { tenantId, orgId } = useParams();
  const { t } = useTranslation();
  const userPermissions = useUserPermissions(orgId, tenantId);
  const { handleMouseDown, isDragging } = useDragDetection();

  const { data: bot } = getCurrentQueryState('fetchBot', { orgId, tenantId });
  const { data: botDeployments } = getCurrentQueryState('fetchBotDeployments', { orgId, tenantId });
  const { data: groups, isFetching: isLoadingGroups } = useFetchGroupsQuery({ orgId, tenantId });
  const [deleteGroup, { isLoading: isDeletingGroup }] = useDeleteGroupMutation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGINATION_SIZE);

  const availablePlatforms = useMemo(() => getSupportedPlatforms(botDeployments), [botDeployments]);

  const onModalOpen = () => {
    setIsModalOpen(true);
  };

  const onModalClose = () => {
    setIsModalOpen(false);
  };

  const onGroupDelete = async (groupId) => {
    deleteGroup({ orgId, tenantId, groupId });
  };

  const handleTableChange = (pagination, _filters, _sorter) => {
    setPageSize(pagination.pageSize);
  };

  const columns = [
    {
      title: t('Name'),
      key: 'label',
      render: record => {
        if (record.syncedAt) {
          return (
            <Fragment>
              {record.label}
              <Tooltip overlayClassName="pointer-events-none" title={`${t('Synced at')}: ${moment(record.syncedAt).tz(bot.timezone).format(DEFAULT_DATETIME_TIMEZONE_FORMAT)}`} placement="right" color="white">
                <SyncOutlined className="ml-2 text-secondary" />
              </Tooltip>
            </Fragment>
          );
        } else {
          return record.label;
        }
      }
    },
    {
      title: t('Members'),
      dataIndex: 'subscribers',
      key: 'subscribers',
      render: subscribers => {
        return subscribers.length;
      }
    },
    {
      title: t('Actions'),
      key: 'actions',
      fixed: 'right',
      width: 110,
      align: 'center',
      render: record => (
        <Fragment>
          <Space onClick={(e) => e.stopPropagation()}>
            <Permissioned
              allowedPermissions={PERMISSIONS_GROUP_EDITOR || PERMISSIONS_GROUP_ADMIN}
              currentPermissions={userPermissions}
              fallbackComponent={null}
            >
              <Fragment>
                <Tooltip
                  overlayClassName="pointer-events-none"
                  title={record.syncedAt ? t('Auto-generated audience are not editable') : t('Edit')}
                  placement={record.syncedAt ? 'right' : 'top'}
                  color="white"
                >
                  <Button
                    type="text"
                    disabled={!!record.syncedAt}
                    shape="circle"
                    icon={<EditIcon />}
                    onClick={() => {
                      setSelectedGroup(record);
                      onModalOpen();
                    }}
                  />
                </Tooltip>
              </Fragment>

              <Popconfirm
                title={t('Are you sure you want to delete this audience?')}
                okText={t('Yes')}
                cancelText={t('No')}
                placement="topRight"
                onConfirm={() => {
                  onGroupDelete(record.id);
                }}
              >
                <Tooltip overlayClassName="pointer-events-none" title={t('Delete')}>
                  <Button
                    type="text"
                    shape="circle"
                    icon={<DeleteIcon />}
                    loading={isDeletingGroup}
                    className="ml-2"
                  />
                </Tooltip>
              </Popconfirm>
            </Permissioned>
          </Space>
        </Fragment>
      )
    }
  ];

  return (
    <Fragment>
      <Row gutter={24} className="mb-4">
        <Col xs={24}>
          <Tooltip
            title={
              availablePlatforms.length === 0
                ? t('There are no platforms available to create an audience. Please create an agent deployment first.')
                : ''
            }
          >
            <Button
              size="large"
              type="primary"
              ghost
              icon={<PlusIcon />}
              className="float-right"
              disabled={availablePlatforms.length === 0}
              onClick={() => {
                setSelectedGroup(null);
                onModalOpen();
              }}
            >
              {t('Add Audience')}
            </Button>
          </Tooltip>
        </Col>
      </Row>

      <Row gutter={24}>
        <Col xs={24}>
          <Table
            size="small"
            rowKey="id"
            onRow={(record) => ({
              onMouseDown: handleMouseDown,
              onClick: () => {
                if(!isDragging() && userPermissions.some(permission => PERMISSIONS_GROUP_EDITOR.includes(permission) || PERMISSIONS_GROUP_ADMIN.includes(permission)) && !record.syncedAt) {
                  setSelectedGroup(record);
                  onModalOpen();
                }
              }
            })}
            rowClassName={(record) => record.syncedAt ? '' : 'cursor-pointer'}
            pagination={{
              size: 'default',
              pageSize: pageSize,
              showSizeChanger: true,
              showTotal: (total) => `${t('Total Audiences')}: ${total}`
            }}
            dataSource={groups?.items}
            columns={columns}
            loading={isLoadingGroups}
            onChange={handleTableChange}
          />
        </Col>
      </Row>

      <SideModal
        title={!selectedGroup ? t('Create New Audience') : t('Edit Audience')}
        open={isModalOpen}
        onClose={onModalClose}
        destroyOnClose
        bodyClassName="p-0"
      >
        <AudienceForm
          isUpdateMode={!!selectedGroup}
          initialGroup={selectedGroup}
          onSubmitSuccess={onModalClose}
          onCancel={onModalClose}
          bodyClassName="px-6 pt-6 flex-grow-1"
          footerClassName="ant-drawer-footer"
        />
      </SideModal>
    </Fragment>
  );
};

export default AudienceTable;
